/**
 * Helipads: where a helicopter stands, ready to be flown.
 *
 * Shortbread carries `aeroway=helipad` as points in its `public_transport`
 * layer, with a name when it has one and nothing else: no outline, no size,
 * no surface. So a pad is drawn as the usual marked disc round the point.
 * A point inside a building outline is a pad on its roof, which is where a
 * hospital's usually is; anywhere else it is on the ground.
 *
 * Like the signs (see `pois.ts`), a pad's height is settled at placement: a
 * roof pad's `y` counts from its building's base, a ground pad's from the
 * ground under its probes.
 */

import { insideRing } from './pois.js';

/** A pad, in chunk metres. */
export interface HelipadSpec {
  x: number;
  z: number;
  /** On a roof, the roof's height over the building's base; on the ground, 0. */
  y: number;
  radius: number;
  /** Which way the H's legs, and the helicopter on it, point. Scene yaw. */
  yaw: number;
  name: string;
  /** Four x,z probes: the building's corners on a roof, the pad's rim on the ground. */
  probes: [number, number, number, number, number, number, number, number];
  onGround: boolean;
}

/** Radius of a pad on the ground, metres: about a 20 m touchdown square's circle. */
export const GROUND_PAD_RADIUS = 9;
/** A roof pad's radius, from as much roof as there is round the point. */
const ROOF_PAD_MIN = 4.5;
const ROOF_PAD_MAX = 9;
/** How far a pad stands proud of what it is on, so it never flickers into it. */
export const PAD_LIFT = 0.06;

export interface HelipadBuildings {
  /** Each solid building's outline, x,z pairs. */
  rings: readonly Float64Array[];
  /** The building buffer: x,y,z triples, [start, count] ranges and four probe corners per range. */
  positions: Float32Array;
  ranges: Uint32Array;
  probes: Float32Array;
}

/**
 * Plan the tile's pads from the helipad points. Points outside the tile —
 * a tile carries a strip of its neighbours' — are its neighbours' to draw.
 */
export function planHelipads(
  points: ReadonlyArray<{ x: number; z: number; name: string }>,
  buildings: HelipadBuildings,
  width: number,
  depth: number,
  seed: number,
): HelipadSpec[] {
  const pads: HelipadSpec[] = [];
  points.forEach((p, i) => {
    if (p.x < 0 || p.x >= width || p.z < 0 || p.z >= depth) return;
    // A stable heading per pad, so a rebuilt tile parks the same way.
    const yaw = (((seed ^ Math.imul(i + 1, 0x9e3779b1)) >>> 0) % 3600) / 3600 * Math.PI * 2;
    const ring = buildings.rings.find(r => insideRing(r, p.x, p.z));
    const range = ring === undefined ? -1 : buildingUnder(buildings, p.x, p.z);
    if (ring !== undefined && range >= 0) {
      let top = -Infinity;
      const start = buildings.ranges[range * 2], count = buildings.ranges[range * 2 + 1];
      for (let v = start; v < start + count; v++) top = Math.max(top, buildings.positions[v * 3 + 1]);
      const room = distanceToRing(ring, p.x, p.z);
      const probes = Array.from(buildings.probes.subarray(range * 8, range * 8 + 8)) as HelipadSpec['probes'];
      pads.push({
        x: p.x, z: p.z, y: top + PAD_LIFT, yaw, name: p.name, probes, onGround: false,
        radius: Math.max(ROOF_PAD_MIN, Math.min(ROOF_PAD_MAX, room)),
      });
      return;
    }
    const r = GROUND_PAD_RADIUS;
    pads.push({
      x: p.x, z: p.z, y: PAD_LIFT, yaw, name: p.name, onGround: true, radius: r,
      probes: [p.x - r, p.z, p.x + r, p.z, p.x, p.z - r, p.x, p.z + r],
    });
  });
  return pads;
}

/** Whether (x, z) is on a ground pad, with `margin` metres round it. */
export function onGroundPad(pads: readonly HelipadSpec[], x: number, z: number, margin = 1): boolean {
  for (const p of pads) if (p.onGround && Math.hypot(x - p.x, z - p.z) < p.radius + margin) return true;
  return false;
}

/**
 * The building range whose four corners enclose (x, z) — the smallest, when
 * a tower's box sits inside its podium's. -1 for none.
 */
function buildingUnder(b: HelipadBuildings, x: number, z: number): number {
  let best = -1, bestArea = Infinity;
  for (let r = 0; r < b.ranges.length / 2; r++) {
    if (b.ranges[r * 2 + 1] === 0) continue;
    const q = b.probes.subarray(r * 8, r * 8 + 8);
    let sign = 0, inside = true;
    for (let c = 0; c < 4 && inside; c++) {
      const ax = q[c * 2], az = q[c * 2 + 1], bx = q[((c + 1) % 4) * 2], bz = q[((c + 1) % 4) * 2 + 1];
      const cross = (bx - ax) * (z - az) - (bz - az) * (x - ax);
      if (cross === 0) continue;
      if (sign === 0) sign = Math.sign(cross);
      else if (Math.sign(cross) !== sign) inside = false;
    }
    if (!inside) continue;
    const area = Math.abs((q[2] - q[0]) * (q[5] - q[1]) - (q[3] - q[1]) * (q[4] - q[0]));
    if (area < bestArea) { bestArea = area; best = r; }
  }
  return best;
}

function distanceToRing(ring: Float64Array, x: number, z: number): number {
  let best = Infinity;
  for (let i = 0, j = ring.length - 2; i < ring.length; j = i, i += 2) {
    const ax = ring[j], az = ring[j + 1], bx = ring[i], bz = ring[i + 1];
    const dx = bx - ax, dz = bz - az;
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / (dx * dx + dz * dz || 1)));
    best = Math.min(best, Math.hypot(ax + dx * t - x, az + dz * t - z));
  }
  return best;
}

/** A pad whose height has been settled: `y` is its surface, chunk metres. */
export interface PlacedHelipad {
  x: number;
  y: number;
  z: number;
  radius: number;
  yaw: number;
  name: string;
  onGround: boolean;
}

/** Segments round a pad's disc. */
const DISC_SEGMENTS = 24;

/**
 * The pads as flat discs: x,y,z positions, u,v for the marking — v along
 * the pad's heading — and indices. Drawn, and stood on.
 */
export function helipadDiscs(pads: readonly PlacedHelipad[]): { positions: Float32Array; uvs: Float32Array; indices: Uint32Array } {
  const per = DISC_SEGMENTS + 1;
  const positions = new Float32Array(pads.length * per * 3);
  const uvs = new Float32Array(pads.length * per * 2);
  const indices = new Uint32Array(pads.length * DISC_SEGMENTS * 3);
  pads.forEach((p, k) => {
    const base = k * per;
    positions.set([p.x, p.y, p.z], base * 3);
    uvs.set([0.5, 0.5], base * 2);
    // Scene forward for a yaw is (-sin, -cos); right of it (cos, -sin).
    const fx = -Math.sin(p.yaw), fz = -Math.cos(p.yaw), rx = Math.cos(p.yaw), rz = -Math.sin(p.yaw);
    for (let s = 0; s < DISC_SEGMENTS; s++) {
      const a = (s / DISC_SEGMENTS) * Math.PI * 2;
      const across = Math.cos(a), along = Math.sin(a);
      const v = base + 1 + s;
      positions.set([p.x + (rx * across + fx * along) * p.radius, p.y, p.z + (rz * across + fz * along) * p.radius], v * 3);
      uvs.set([0.5 + across / 2, 0.5 + along / 2], v * 2);
      // Wound to face up.
      const o = (k * DISC_SEGMENTS + s) * 3;
      indices[o] = base;
      indices[o + 1] = base + 1 + ((s + 1) % DISC_SEGMENTS);
      indices[o + 2] = v;
    }
  });
  return { positions, uvs, indices };
}
